import {AiVideoAudio, AiVideoQuality} from '@Panels/ai-video-panel/ai-video-panel.types';
import {stringToEnum} from '@Utils/string.util';
import {
  DEFAULT_POSTER_ANIMATION_AUDIO,
  DEFAULT_POSTER_ANIMATION_QUALITY,
} from '@Components/poster-editor/components/poster-editing-side-panel/components/ai-poster-animation-panel/ai-poster-animation-panel.types';

const ON_MY_DESIGN_EXPANDED_STORAGE_KEY = 'pmw_color_picker_on_my_design_expanded';
const POSTER_ANIMATION_PREFERENCES_STORAGE_KEY = 'pmw_poster_animation_preferences';
const POSTER_ANIMATION_GENERATION_COUNT_STORAGE_KEY = 'pmw_poster_animation_generation_count';
const SHOW_ITEM_TIMELINE_TRACK_STORAGE_KEY = 'pmw_super_user_show_item_timeline_track';

export interface PosterAnimationPreferences {
  quality: AiVideoQuality;
  audio: AiVideoAudio;
}

export const getPersistedIsOnMyDesignExpanded = (): boolean => {
  try {
    return localStorage.getItem(ON_MY_DESIGN_EXPANDED_STORAGE_KEY) === 'true';
  } catch {
    // localStorage may be unavailable
  }
  return false;
};

export const persistIsOnMyDesignExpanded = (isExpanded: boolean): void => {
  try {
    localStorage.setItem(ON_MY_DESIGN_EXPANDED_STORAGE_KEY, String(isExpanded));
  } catch {
    // localStorage may be unavailable
  }
};

export const getPersistedShowItemTimelineTrack = (): boolean => {
  try {
    return localStorage.getItem(SHOW_ITEM_TIMELINE_TRACK_STORAGE_KEY) === 'true';
  } catch {
    // localStorage may be unavailable
  }
  return false;
};

export const persistShowItemTimelineTrack = (showItemTimelineTrack: boolean): void => {
  try {
    localStorage.setItem(SHOW_ITEM_TIMELINE_TRACK_STORAGE_KEY, String(showItemTimelineTrack));
  } catch {
    // localStorage may be unavailable
  }
};

export const getPersistedPosterAnimationPreferences = (): PosterAnimationPreferences => {
  try {
    const stored = localStorage.getItem(POSTER_ANIMATION_PREFERENCES_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as {quality?: string; audio?: string};
      return {
        quality: stringToEnum(parsed.quality, Object.values(AiVideoQuality), DEFAULT_POSTER_ANIMATION_QUALITY),
        audio: stringToEnum(parsed.audio, Object.values(AiVideoAudio), DEFAULT_POSTER_ANIMATION_AUDIO),
      };
    }
  } catch {
    // localStorage may be unavailable
  }
  return {quality: DEFAULT_POSTER_ANIMATION_QUALITY, audio: DEFAULT_POSTER_ANIMATION_AUDIO};
};

export const persistPosterAnimationPreferences = (preferences: PosterAnimationPreferences): void => {
  try {
    localStorage.setItem(POSTER_ANIMATION_PREFERENCES_STORAGE_KEY, JSON.stringify(preferences));
  } catch {
    // localStorage may be unavailable
  }
};

// Seeds the finalize-hint visibility at first paint, before the async generations fetch resolves.
export const getPersistedPosterAnimationCount = (): number | null => {
  try {
    const stored = localStorage.getItem(POSTER_ANIMATION_GENERATION_COUNT_STORAGE_KEY);
    if (stored !== null) {
      const parsed = Number.parseInt(stored, 10);
      if (Number.isFinite(parsed) && parsed >= 0) {
        return parsed;
      }
    }
  } catch {
    // localStorage may be unavailable
  }
  return null;
};

export const persistPosterAnimationCount = (count: number): void => {
  try {
    localStorage.setItem(POSTER_ANIMATION_GENERATION_COUNT_STORAGE_KEY, `${count}`);
  } catch {
    // localStorage may be unavailable
  }
};
